import type { ChartLikeProtocol, PieChartOptionProtocol } from '@lighthouse/core'

import type { ECOption } from '../../components'
import { defaultColors, legendOffset, legendWidth } from './common'

type FunnelSeriesDataItem = {
    value: number
    name: string
}

type LegendStat = {
    value: number
    percent: string
}

const normalizeChartNumericValue = (value: unknown) => {
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : 0
    }

    if (typeof value === 'string') {
        const normalizedValue = value.trim().replace(/,/g, '')
        if (!normalizedValue) {
            return 0
        }

        const parsedValue = Number(normalizedValue)
        return Number.isFinite(parsedValue) ? parsedValue : 0
    }

    return 0
}

const formatLegendPercent = (percent: number) => {
    if (!Number.isFinite(percent) || percent <= 0) {
        return '0%'
    }

    return `${Number(percent.toFixed(2))}%`
}

const getSeriesData = (chartData: ChartLikeProtocol): FunnelSeriesDataItem[] => {
    const { series, xAxis } = chartData

    if (series.length === 0) {
        return []
    }

    const [firstSeries] = series
    const { value: seriesValue } = firstSeries

    if (seriesValue.length > 1) {
        return xAxis.map((item, index) => ({
            value: normalizeChartNumericValue(seriesValue[index]),
            name: item[0] || '空'
        }))
    }

    if (seriesValue.length === 1) {
        return series.map(item => ({
            value: normalizeChartNumericValue(item.value?.[0]),
            name: item.name
        }))
    }

    return series.map(item => ({
        value: normalizeChartNumericValue(item.value?.[0]),
        name: item.name
    }))
}

const getLegendStats = (seriesData: FunnelSeriesDataItem[]) => {
    const valueByName = new Map<string, number>()

    seriesData.forEach(({ name, value }) => {
        valueByName.set(name, (valueByName.get(name) || 0) + value)
    })

    const total = Array.from(valueByName.values()).reduce((sum, value) => sum + value, 0)

    return new Map<string, LegendStat>(
        Array.from(valueByName.entries()).map(
            ([name, value]) =>
                [
                    name,
                    {
                        value,
                        percent: formatLegendPercent(total > 0 ? (value / total) * 100 : 0)
                    }
                ] as const
        )
    )
}

const formatLegendLabel = (name: string, legendStats: Map<string, LegendStat>) => {
    const stat = legendStats.get(name)

    if (!stat) {
        return name
    }

    return `${name} ${stat.value} ${stat.percent}`
}

const getFunnelCommonOption = (params: PieChartOptionProtocol) => {
    const { showLegend, colors, showHeadTitle, title } = params
    const commonOption: Pick<ECOption, 'color' | 'legend' | 'title'> = {
        color: colors?.length > 0 ? colors : defaultColors,
        legend: {
            show: showLegend
        },
        title: {
            text: showHeadTitle && title ? title : undefined,
            textStyle: {
                fontSize: 24,
                fontWeight: 500,
                color: '#101828'
            }
        }
    }
    return commonOption
}

const getPieLegend = (params: PieChartOptionProtocol, chartData: ChartLikeProtocol): ECOption['legend'] => {
    const { showLegend, legendTranspose, dimensions } = params
    const { legend, xAxis } = chartData
    const data = dimensions.length > 0 ? xAxis.map(item => item[0]).filter(Boolean) : legend
    const legendStats = getLegendStats(getSeriesData(chartData))
    const legendConfigurator: ECOption['legend'] = {
        type: 'scroll',
        show: showLegend,
        data,
        bottom: legendOffset,
        icon: 'circle',
        formatter: name => formatLegendLabel(name, legendStats)
    }
    switch (legendTranspose) {
        case 'top': {
            legendConfigurator.top = legendOffset
            break
        }
        case 'bottom': {
            legendConfigurator.bottom = legendOffset
            break
        }
        case 'left': {
            legendConfigurator.left = legendOffset
            legendConfigurator.top = 'center'
            legendConfigurator.width = legendWidth
            legendConfigurator.orient = 'vertical'
            break
        }
        case 'right': {
            legendConfigurator.right = legendOffset
            legendConfigurator.width = legendWidth
            legendConfigurator.top = 'center'
            legendConfigurator.orient = 'vertical'
            break
        }
        default: {
            break
        }
    }

    return legendConfigurator
}

const getSeries = (chartData: ChartLikeProtocol): ECOption['series'] => {
    const { legend } = chartData
    const seriesData = getSeriesData(chartData)
    if (seriesData.length === 0) {
        return {}
    }

    const [seriesName] = legend

    return {
        name: seriesName,
        type: 'funnel',
        data: seriesData,
        left: '10%',
        top: 60,
        bottom: 60,
        width: '80%',
        gap: 2,
        emphasis: {
            focus: 'self'
        },
        blur: {
            itemStyle: {
                opacity: 0.6
            }
        }
    }
}

const getFunnelOption = (params: PieChartOptionProtocol, chartData?: ChartLikeProtocol, themeColors?: string[]) => {
    if (!chartData) {
        return {}
    }
    const commonOption = getFunnelCommonOption(params)
    const mergedOption: ECOption = {
        tooltip: {
            show: true,
            formatter: params => {
                const param = Array.isArray(params) ? params[0] : params
                const { marker, name, value, percent, seriesName } = param
                return `<div style="margin-bottom: 12px; font-weight: bold">
                            ${name || '空'}
                        </div>
                        ${marker} ${seriesName}：${String(value)} (${percent}%)`
            }
        },
        ...commonOption,
        legend: getPieLegend(params, chartData),
        series: getSeries(chartData)
    }
    return mergedOption
}

export default getFunnelOption
